import { Injectable, inject } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { Checkout } from './models/checkout';
import { FromScreen } from 'src/app/models/from-screen';
import { catchError } from 'rxjs/operators';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

const HTTP_OPTIONS_URLENCODED = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class CheckoutsService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly userService = inject(UserService);

  getCheckouts(): Observable<Checkout[]> {
    const apiURL = `${this.apiService.API_URL}/patrons/${this.userService.getUserId()}/checkouts`;

    return this.http.get<Checkout[]>(apiURL);
  }

  postCheckout(recordId: string, from: FromScreen, issueDate?: string, rhId?: string): Observable<Checkout> {
    if (rhId) {
      return this.checkoutRssItem(recordId, from, rhId);
    }

    const body = `patronId=${this.userService.getUserId()}&from=${from}` + (issueDate ? `&issueDate=${issueDate}` : '');
    return this.http
      .post<Checkout>(`${this.apiService.API_URL}/records/${recordId}/checkout`, body, HTTP_OPTIONS_URLENCODED)
      .pipe(
        tap(() => {
          this.analyticsService.sendCheckoutEvent(true);
        }),
        catchError((error) => {
          this.analyticsService.sendCheckoutEvent(false);
          return throwError(() => error);
        })
      );
  }

  private checkoutRssItem(recordId: string, from: FromScreen, rhId: string): Observable<Checkout> {
    const body = `patronId=${this.userService.getUserId()}&from=${from}`;
    return this.http.post<Checkout>(
      `${this.apiService.API_URL}/records/${recordId}/items/${rhId}/checkout`,
      body,
      HTTP_OPTIONS_URLENCODED
    );
  }

  postRenewCheckout(checkoutId: string): Observable<Checkout> {
    const body = `patronId=${this.userService.getUserId()}`;
    return this.http
      .post<Checkout>(`${this.apiService.API_URL}/checkouts/${checkoutId}/renew`, body, HTTP_OPTIONS_URLENCODED)
      .pipe(
        tap(() => {
          this.analyticsService.sendRenewalEvent(true);
        }),
        catchError((error) => {
          this.analyticsService.sendRenewalEvent(false);
          return throwError(() => error);
        })
      );
  }

  postReturnCheckout(checkoutId: string): Observable<Checkout> {
    const body = `patronId=${this.userService.getUserId()}`;
    return this.http
      .post<Checkout>(`${this.apiService.API_URL}/checkouts/${checkoutId}/return`, body, HTTP_OPTIONS_URLENCODED)
      .pipe(
        tap(() => {
          this.analyticsService.sendUserActionEvent('return_checkout', true);
        }),
        catchError((error) => {
          this.analyticsService.sendUserActionEvent('return_checkout', false);
          return throwError(() => error);
        })
      );
  }
}
